import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { useAccount } from 'wagmi';
import { useModalState } from '../../hooks/useModalState';
import { usePositionFills } from '../../hooks/usePositionFills';
import { usePositionById } from '../../hooks/usePositions';
import { useProductsById } from '../../hooks/useProducts';
import { formatDate, formatDollar, formatNumber } from '../../lib/formatters';
import { getDecimalPlaces } from '../../lib/math';
import { getPositionFillsTradeLabel } from '../../lib/orderHelpers';
import { isPositionFillOpenOrClose } from '../../lib/positionHelpers';
import { toLowerCase } from '../../lib/stringHelpers';
import { OrderSide } from '../../types/generated';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { getRowLeftBorderClassName } from '../Table/commonTableParams';
import { StateTable } from '../Table/Table';

// Constants for pagination
const ITEMS_PER_PAGE = 8;
const ITEMS_PER_FETCH = 100;

type TableData = {
  side: OrderSide;
  size: bigint;
  price: bigint;
  reduceOnly: boolean;
  time: number;
  tradeLabel: string;
  type: string;
  baseTokenName: string;
  sizeDecimals: number;
  priceDecimals: number;
};

type PositionFillsModalUiProps = {
  isOpen: boolean;
  onClose: () => void;
  isPending: boolean;
  isFetching: boolean;
  isConnected: boolean;
  allItems: TableData[];
  fetchNextBatch: () => void;
  onSortingChange: (sorting: SortingState) => void;
  sortingState: { id: string; desc: boolean }[];
};

const PositionFillsModalUi = ({
  onClose,
  isOpen,
  allItems,
  isFetching,
  isPending,
  isConnected,
  fetchNextBatch,
  onSortingChange,
  sortingState,
}: PositionFillsModalUiProps) => {
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TableData>();

    return [
      columnHelper.accessor(({ side, tradeLabel }) => ({ side, tradeLabel }), {
        id: 'trade_label_side',
        header: 'Trade Type',
        enableSorting: false,
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
        cell: (info) => {
          const { tradeLabel, side } = info.getValue();
          return (
            <div
              className={twJoin(
                'flex items-center text-nowrap font-medium lg:min-h-[40px]',
                side === OrderSide.BUY ? 'text-contentGreen' : 'text-contentRed'
              )}
            >
              {tradeLabel}
            </div>
          );
        },
      }),
      columnHelper.accessor(
        ({ size, baseTokenName, sizeDecimals }) => ({ size, baseTokenName, sizeDecimals }),
        {
          id: 'size_baseTokenName',
          header: 'Filled Size',
          enableSorting: false,
          meta: {
            accordion: {
              showInTrigger: true,
            },
          },
          cell: (info) => {
            const { size, baseTokenName, sizeDecimals } = info.getValue();
            return (
              <span className="text-nowrap">
                {formatNumber(size, {
                  minimumFractionDigits: sizeDecimals,
                  maximumFractionDigits: sizeDecimals,
                })}{' '}
                {baseTokenName}
              </span>
            );
          },
        }
      ),
      columnHelper.accessor(({ price, priceDecimals }) => ({ price, priceDecimals }), {
        id: 'price_with_decimals',
        header: 'Filled Price',
        enableSorting: false,
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
        cell: (info) => {
          const { price, priceDecimals } = info.getValue();
          return formatDollar(price, {
            minimumFractionDigits: priceDecimals,
            maximumFractionDigits: priceDecimals,
          });
        },
      }),
      columnHelper.accessor('type', {
        header: 'Order Type',
        enableSorting: false,
        meta: { cellContainerClassName: 'capitalize' },
      }),
      columnHelper.accessor('time', {
        header: 'Time',
        cell: (info) => formatDate(info.getValue()),
        meta: {
          align: 'right',
        },
        enableSorting: true,
      }),
    ];
  }, []);

  const table = useReactTable({
    columns,
    data: allItems,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: true,
    enableMultiSort: false,
    initialState: {
      pagination: {
        pageSize: ITEMS_PER_PAGE,
        pageIndex: 0,
      },
    },
    state: {
      sorting: sortingState,
    },
    manualSorting: true,
    onSortingChange: (updater) => {
      const sortingArr = typeof updater === 'function' ? updater(sortingState) : updater;
      const sorting = sortingArr[0];
      onSortingChange([{ id: 'time', desc: sorting?.desc || false }]);
    },
    enableSortingRemoval: false,
    autoResetPageIndex: false,
    meta: {
      getRowClassName: getRowLeftBorderClassName,
    },
  });

  return (
    <ResponsiveModal
      onClose={onClose}
      open={isOpen}
      title="Details"
      panelClassName="sm:h-[544px] sm:w-fit sm:min-w-[575px]"
      childrenContainerClassName="h-full"
    >
      <StateTable
        table={table}
        isPending={isPending}
        isFetching={isFetching}
        isConnected={isConnected}
        fetchNextPage={fetchNextBatch}
      />
    </ResponsiveModal>
  );
};

export const PositionFillsModal = () => {
  const { isConnected } = useAccount();
  const { openOrClosed, positionId } = useModalState((state) => state.positionFillsModal);
  const setPositionFillsModal = useModalState((state) => state.setPositionFillsModal);
  const productsById = useProductsById();
  const { data: position } = usePositionById(positionId);

  // Create state for sorting - always start with 'desc' for time column
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const product = position && productsById ? productsById[position.productId] : undefined;

  const positionFillsQuery = usePositionFills({
    positionId: position?.id,
    limit: ITEMS_PER_FETCH, // Fetch 100 items at a time
    order: sortOrder,
  });

  // Flatten all items from fetched pages
  const allItems = useMemo(() => {
    if (!product || !position || !positionFillsQuery.data?.pages) return [];

    return positionFillsQuery.data.pages.flatMap((page, pageIndex) => {
      return page.fills.map((fill, fillIndex) => {
        const { isLastFill, isFirstFill } = isPositionFillOpenOrClose({
          pageIndex,
          fillIndex,
          sortOrder,
          fillsLength: page.fills.length,
          pagesLength: positionFillsQuery.data.pages.length,
          hasNextPage: positionFillsQuery.hasNextPage,
        });

        return {
          side: fill.side,
          size: fill.filled,
          price: fill.price,
          reduceOnly: fill.reduceOnly,
          time: fill.createdAt,
          type: toLowerCase(fill.type),
          tradeLabel: getPositionFillsTradeLabel({
            side: fill.side,
            positionOpenSide: position.side,
            isFirstFill,
            isLastFill,
          }),
          baseTokenName: product.baseTokenName,
          sizeDecimals: getDecimalPlaces(product.lotSize),
          priceDecimals: getDecimalPlaces(product.tickSize),
        };
      });
    });
  }, [
    product,
    position,
    positionFillsQuery.data?.pages,
    positionFillsQuery.hasNextPage,
    sortOrder,
  ]);

  const fetchNextBatch = useCallback(async () => {
    if (positionFillsQuery.hasNextPage && !positionFillsQuery.isFetchingNextPage) {
      await positionFillsQuery.fetchNextPage();
    }
  }, [positionFillsQuery]);

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    const sorting = newSorting[0];
    // This table only allows ordering by time
    if (!sorting || sorting.id !== 'time') return;

    setSortOrder(sorting.desc ? 'desc' : 'asc');
  }, []);

  if (!position) return null;

  return (
    <PositionFillsModalUi
      isPending={positionFillsQuery.isPending}
      isFetching={positionFillsQuery.isFetching}
      isConnected={isConnected}
      allItems={allItems}
      isOpen={openOrClosed === 'open'}
      onClose={() => {
        setPositionFillsModal({ openOrClosed: 'closed', positionId: undefined });
      }}
      fetchNextBatch={fetchNextBatch}
      onSortingChange={handleSortingChange}
      sortingState={[{ id: 'time', desc: sortOrder === 'desc' }]}
    />
  );
};
